import React from "react";
import styled, { css } from "styled-components";
import { History } from "history";
import { upperCase } from "lodash";
import moment from "moment";
import { RouteComponentProps, withRouter } from "react-router";
import { Link as RouterLink } from "react-router-dom";
import { X } from "@sonder/ui/source/icons";
import { Avatar } from "@sonder/ui/source/styled/components/Avatar";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { colors } from "@sonder/ui/source/styled/theme";
import { useStore as useAuthStore } from "auth/store";
import {
  GetNotificationResourceCommentTarget,
  GetUserNotification,
  GetUserNotificationResourceCleanActivity,
  GetUserNotificationResourceComment,
  GetUserNotificationResourceTask,
  GetUserNotificationResourceTaskActivity,
  GetUserNotificationResourceTaskActivityMetadataAssigneeIdChangeMetadata,
  GetUserNotificationResourceTaskActivityMetadataChangedTagMetadata,
  GetUserNotificationResourceTaskActivityMetadataDueAtChangeMetadata,
  GetUserNotificationResourceTaskActivityMetadataEtaChangeMetadata,
  GetUserNotificationResourceTaskActivityMetadataNameChangeMetadata,
  GetUserNotificationResourceTaskActivityMetadataPriorityChangeMetadata,
  GetUserNotificationResourceTaskActivityMetadataScheduleChangeMetadata,
  GetUserNotificationResourceTaskActivityMetadataStatusChangeMetadata,
  GetUserNotificationResourceUser,
  GetUserNotificationTask,
} from "core/queries/GetNotifications";
import {
  getCleaninRequestInternalRef as getCleaningRequestInternalRef,
  getTaskInternalRef,
} from "core/queries/mappers";
import { ParsedText } from "tasks/components/ParsedText";
import { parseGraphQLId, truncateString } from "tasks/helpers";
import { NotificationReason } from "typings/globalGQLTypes";

const defaultUser: GetUserNotificationResourceUser = {
  __typename: "User",
  id: "User|Unknown",
  name: "(Unknown)",
  avatar: null,
};

type Resource =
  | GetUserNotificationResourceCleanActivity
  | GetUserNotificationResourceComment
  | GetUserNotificationResourceTask
  | GetUserNotificationResourceTaskActivity;

// helpers
export const isResourceACleanActivity = (
  resource: Resource,
): resource is GetUserNotificationResourceCleanActivity =>
  "cleaningRequest" in resource;

export const isResourceAComment = (
  resource: Resource,
): resource is GetUserNotificationResourceComment => "target" in resource;

export const isResourceATask = (
  resource: Resource,
): resource is GetUserNotificationResourceTask => "channel" in resource;

export const isResourceATaskActivity = (
  resource: Resource,
): resource is GetUserNotificationResourceTaskActivity => "task" in resource;

const getAssociatedSubject = (notification: GetUserNotification) => {
  const { resource } = notification;
  if (!resource) return null;
  return (
    (isResourceACleanActivity(resource) && resource.cleaningRequest) ||
    (isResourceAComment(resource) && resource.target) ||
    (isResourceATask(resource) && resource) ||
    (isResourceATaskActivity(resource) &&
      (resource.task.parentTask || resource.task)) ||
    null
  );
};

const getNotificationUser = (notification: GetUserNotification) => {
  const { resource } = notification;
  if (!resource) return defaultUser;
  return (
    (isResourceACleanActivity(resource) && resource.user) || // CleanActivity
    (isResourceAComment(resource) && resource.author) || // Comment
    (isResourceATask(resource) && resource.author) || // Task
    (isResourceATaskActivity(resource) && resource.user) || // TaskActivity
    defaultUser
  );
};

const generateCleanActivityContent = (notification: GetUserNotification) => {
  const resource =
    notification.resource as GetUserNotificationResourceCleanActivity;
  return resource?.body;
};

const generateCommentContent = (notification: GetUserNotification) => {
  const {
    state: { user },
  } = useAuthStore();
  if (!user) return null;

  const userId = user.id;
  const commentResource =
    notification?.resource as GetUserNotificationResourceComment;
  const commentBody = commentResource?.body;
  const userName = getNotificationUser(notification).name;
  const currentAssigneeId = (
    commentResource?.target as GetNotificationResourceCommentTarget
  )?.assignee?.id;
  const isCurrentAssigneeYou = userId === currentAssigneeId;
  if (!commentBody) return null;
  const truncatedBody = truncateString(commentBody, 140);
  return `${userName} added a comment on ${
    isCurrentAssigneeYou
      ? "a task you are assigned to"
      : "a task not assigned to you"
  }: ${truncatedBody}`;
};

const generateActivityContent = (notification: GetUserNotification) => {
  const {
    state: { user },
  } = useAuthStore();
  if (!user) return null;

  const userId = user.id;
  const taskActivityResource =
    notification?.resource as GetUserNotificationResourceTaskActivity;
  const activityType = taskActivityResource.activityType;
  const tagName = (
    taskActivityResource.metadata as GetUserNotificationResourceTaskActivityMetadataChangedTagMetadata
  )?.tag?.name;
  const userName = getNotificationUser(notification).name;
  const taskName = taskActivityResource.task.name;
  const parentTask = taskActivityResource.task.parentTask;
  switch (activityType) {
    case "added_tag":
      return `${userName} added tag: ${tagName}`;
    case "assignee_id_change": {
      const assigneeMetadata =
        taskActivityResource.metadata as GetUserNotificationResourceTaskActivityMetadataAssigneeIdChangeMetadata;
      const prevAssignee = assigneeMetadata?.prevAssignee;
      const newAssignee = assigneeMetadata?.newAssignee;
      const hasNewAssignee = Boolean(newAssignee);
      const relevantAssignee = hasNewAssignee ? newAssignee : prevAssignee;
      const relevantAssigneeId = newAssignee && newAssignee.id;
      const isRelevantAssigneeYou = relevantAssigneeId === userId;
      const relevantAssigneeName = isRelevantAssigneeYou
        ? "you"
        : relevantAssignee && relevantAssignee.name;
      const action = hasNewAssignee ? "assigned" : "unassigned";
      return `${userName} ${action} ${relevantAssigneeName}`;
    }
    case "due_at_change": {
      const dueAtMetadata =
        taskActivityResource.metadata as GetUserNotificationResourceTaskActivityMetadataDueAtChangeMetadata;
      const newDueAt = dueAtMetadata.newDueAt;
      return newDueAt
        ? `${userName} set due date to: ${moment(newDueAt).format("MMM D")}`
        : `${userName} removed due date`;
    }
    case "eta_change": {
      const etaMetadata =
        taskActivityResource.metadata as GetUserNotificationResourceTaskActivityMetadataEtaChangeMetadata;
      const newEta = etaMetadata.newEta;
      return newEta
        ? `${userName} set ETA: ${moment(newEta).fromNow()}`
        : `${userName} removed ETA.`;
    }
    case "get_task_created":
      return `${userName} created an action: ${taskName}`;
    case "name_change": {
      const nameChangeMetadata =
        taskActivityResource.metadata as GetUserNotificationResourceTaskActivityMetadataNameChangeMetadata;
      const newName = nameChangeMetadata.newName;
      return `${userName} renamed task to ${newName}`;
    }
    case "permission_to_enter_change":
      return `${userName} changed Permission to Enter`; // no metadata :(
    case "priority_change": {
      const priorityMetadata =
        taskActivityResource.metadata as GetUserNotificationResourceTaskActivityMetadataPriorityChangeMetadata;
      const newPriority = priorityMetadata.newPriority;
      return newPriority
        ? `${userName} set priority to: P${newPriority}`
        : `${userName} cleared priority`;
    }
    case "recurrence_change":
      // TODO!
      return "Changed Recurrence";
    case "removed_tag":
      return `${userName} removed the ${tagName} tag`;
    case "schedule_change": {
      const scheduleChangeMetadata =
        taskActivityResource.metadata as GetUserNotificationResourceTaskActivityMetadataScheduleChangeMetadata;
      const previousAssignees = scheduleChangeMetadata.prevAssignees;
      const assigneeNames = previousAssignees?.map(assignee => {
        return assignee.id === userId ? "you" : assignee.name;
      });

      // When a clean is unscheduled and was assigned, show the previous assignee names
      if (assigneeNames && assigneeNames.length > 0) {
        const assigneesContainsYou = !!previousAssignees?.find(
          assignee => assignee.id === userId,
        );
        const isYouOnlyAssignee =
          assigneeNames.length === 1 && assigneesContainsYou;
        const verb =
          isYouOnlyAssignee || assigneeNames.length > 1 ? "were" : "was";
        const userNames = assigneeNames.join(", ");
        const reason = scheduleChangeMetadata.reason;

        return `${userName} ${reason} and ${userNames} ${verb} unassigned`;
      }

      return `${taskActivityResource.body}`;
    }
    case "status_change": {
      const statusChangeMetadata =
        taskActivityResource.metadata as GetUserNotificationResourceTaskActivityMetadataStatusChangeMetadata;
      const newStatus = statusChangeMetadata.newStatus;
      const action = newStatus === "complete" ? "completed" : "re-opened";
      return parentTask
        ? `${userName} ${action} action: ${parentTask.name}`
        : `${userName} ${action} the task`;
    }
    default:
      return null;
  }
};

const generateTaskContent = (notification: GetUserNotification) => {
  const taskResource =
    notification?.resource as GetUserNotificationResourceTask;

  const { author, channel, name } = taskResource;
  const authorText = author ? ` by ${author.name}` : "";

  return `${name} was created in ${upperCase(channel)}${authorText}`;
};

const generateContent = (notification: GetUserNotification) => {
  const { resource } = notification;
  if (!resource) return null;

  if (isResourceACleanActivity(resource))
    return generateCleanActivityContent(notification);
  if (isResourceAComment(resource)) return generateCommentContent(notification);
  if (isResourceATask(resource)) return generateTaskContent(notification);
  if (isResourceATaskActivity(resource))
    return generateActivityContent(notification);

  return null;
};

const generateReasonCopy = (reason: string) => {
  switch (reason) {
    case NotificationReason.assignee:
      return "Assigned";
    case NotificationReason.buildingSupervisor:
      return "Building Supervisor";
    case NotificationReason.lead:
      return "Lead";
    case NotificationReason.hkManager:
      return "HK Manager";
    case NotificationReason.mention:
      return "Mentioned";
    case NotificationReason.parentAssignee:
      return "Parent Assigned";
    case NotificationReason.previousAssignee:
      return "Previously Assigned";
    case NotificationReason.subscriber:
      return "Subscribed";
    default: {
      return null;
    }
  }
};

const generateSubjectContent = (
  notification: GetUserNotification,
  history: History,
) => {
  const { resource } = notification;
  const subject = getAssociatedSubject(notification);
  if (!resource || !subject) return null;

  if (
    isResourceACleanActivity(resource) &&
    subject.__typename === "CleaningRequest"
  ) {
    const url = "/schedule";
    return {
      dueAt: subject.cleaning?.scheduledStart || subject.startAt,
      internalRef: getCleaningRequestInternalRef(subject),
      onClick: () => {
        history.push(url);
        // TODO: Filter here!
      },
      name: resource.title,
      url,
    };
  }

  if (subject.__typename === "Task") {
    const url = `/tasks/${parseGraphQLId(subject.id)}/activity`;
    return {
      dueAt: subject.dueAt,
      internalRef: getTaskInternalRef(subject as GetUserNotificationTask),
      onClick: () => {
        history.push(url);
      },
      name: subject.name,
      url,
    };
  }

  return null;
};

interface ActivityProps {
  notification: GetUserNotification;
  className?: string;
}

const Activity = ({ notification, className }: ActivityProps) => {
  const { resource } = notification;
  if (!resource) return null;

  return (
    <div className={className}>
      <ParsedText>{generateContent(notification)}</ParsedText>
    </div>
  );
};

const StyledActivity = styled(Activity)`
  margin: 0.125rem 0;
`;

const DefaultAvatar = styled(Avatar)`
  opacity: 0;
  pointer-events: none;
`;

const CommentNotificationCardStyles = css`
  align-items: center;
  box-sizing: border-box;
  color: inherit;
  cursor: pointer;
  display: flex;
  font-size: 0.8rem;
  flex-direction: row;
  justify-content: space-between;
  min-height: 4.5rem;
  min-width: 0;
  padding: 0.75rem 1.71rem;
  position: relative;
  text-decoration: none;
`;

export const NotificationCardFragment = styled.div`
  ${CommentNotificationCardStyles}

  ${CircleButton} {
    opacity: 0;
  }

  &:hover,
  &:focus {
    background: ${colors.grayF};

    ${CircleButton} {
      opacity: 1;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-left: 2rem;
  min-width: 0;

  a {
    word-break: break-all;
  }
`;

export const EmptyNotificationCardFragment = styled.div`
  ${CommentNotificationCardStyles}

  background: ${colors.grayF};

  ${Content} {
    margin-left: 0;
  }
`;

const Eyebrow = styled.div`
  color: ${colors.gray6};
  font-size: 0.8rem;
  margin: 0.125rem 0;
`;

const Header = styled(RouterLink)`
  color: inherit;
  display: block;
  font-size: 1rem;
  font-weight: bold;
  margin: 0.125rem 0;
  overflow: hidden;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  align-self: stretch;
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: 1rem;
`;

const Timestamp = styled.div`
  color: ${colors.grayA};
  margin: 0.125rem 0;
`;

interface DueAtProps {
  isPastDue?: boolean;
  isVisible: boolean;
}

const DueAt = styled.span<DueAtProps>`
  color: ${props => (props.isPastDue ? colors.redD : colors.grayA)};
  visibility: ${props => (props.isVisible ? "visible" : "hidden")};
  white-space: nowrap;
`;

interface NotificationCardProps extends RouteComponentProps {
  className?: string;
  onClick?(): void;
  onArchive?(): void;
  notification: GetUserNotification;
}

export const NotificationCard = withRouter(
  ({
    className,
    onClick,
    history,
    onArchive,
    notification,
  }: NotificationCardProps) => {
    const {
      state: { user },
    } = useAuthStore();
    if (!user || !notification) return null;
    const reasons = notification.reasons || [];
    const isArchived = notification.archived;
    const activityTime = notification.activityTime;
    const activityTimeMoment = activityTime && moment(activityTime);
    const subjectContent = generateSubjectContent(notification, history);
    if (!subjectContent) return null;

    const {
      dueAt,
      internalRef,
      onClick: onSubjectClick,
      name,
      url,
    } = subjectContent;
    const dueAtMoment = dueAt && moment(dueAt);
    const isToday =
      dueAtMoment &&
      dueAtMoment.clone().startOf("day").isSame(moment().startOf("day"));
    const isPastDue = Boolean(
      dueAtMoment && dueAtMoment.isSameOrBefore(moment()),
    );
    const notificationUser = getNotificationUser(notification);
    const isDefaultUser = defaultUser.id === notificationUser.id;
    const AvatarComponent = isDefaultUser ? DefaultAvatar : Avatar;
    const onHandleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (onSubjectClick) onSubjectClick();

      if (onClick) onClick();
    };
    const onHandleArchive = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onArchive) onArchive();
    };
    if (isArchived)
      return (
        <EmptyNotificationCardFragment>
          <Content>Your notification has been deleted from the feed.</Content>
          {onArchive && <Link onClick={onHandleArchive}>Undo</Link>}
        </EmptyNotificationCardFragment>
      );

    return (
      <NotificationCardFragment className={className} onClick={onHandleClick}>
        <AvatarComponent
          imageUrl={notificationUser.avatar?.url}
          name={notificationUser.name || "(Unknown)"}
        />
        <Content>
          {internalRef && <Eyebrow>{internalRef}</Eyebrow>}
          <Header
            title={reasons.map(generateReasonCopy).join(", ")}
            to={url}
            onClick={onHandleClick}
          >
            {name}
          </Header>

          <StyledActivity notification={notification} />

          {activityTimeMoment && (
            <Timestamp> {activityTimeMoment.fromNow()}</Timestamp>
          )}
        </Content>
        <Actions>
          <DueAt isPastDue={isPastDue} isVisible={dueAt}>
            {dueAtMoment
              ? dueAtMoment.format(isToday ? "hh:mm a" : "MMM D")
              : "Invalid"}
          </DueAt>
          <CircleButton backgroundVariant="light" onClick={onHandleArchive}>
            <X />
          </CircleButton>
          <span>&nbsp;</span>
        </Actions>
      </NotificationCardFragment>
    );
  },
);
